import React, { useState, useContext, useEffect } from "react";
import {
  Card,
  Typography,
  Tag,
  Button,
  InputNumber,
  Divider,
  Row,
  Col,
  Statistic,
  Upload,
  message,
  Alert,
  Space,
  Radio,
  Collapse,
  Spin,
} from "antd";
import {
  DownloadOutlined,
  UploadOutlined,
  BankOutlined,
  UserOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  CloseCircleOutlined,
  WalletOutlined,
  FileProtectOutlined,
} from "@ant-design/icons";

import { GetFeeChallanDD } from "./PDFLayout";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { UserContext } from "../../UserContext";
import { ConfigContext } from "../../ConfigContext";
import { GetUnAssignedStudents, GetRowers } from "../../APIManager";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;

export default function NewPaymentPage() {
  const [numStudents, setNumStudents] = useState(0);
  const [regType, setRegType] = useState("individual"); // 'individual' or 'school'
  const [fileList, setFileList] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("bank"); // 'cash', 'bank', 'cheque'

  const [unassignedStudents, setUnassignedStudents] = useState({});
  const [rowers, SetRowers] = useState([]);
  const [user, setUser] = useContext(UserContext);
  let [config, setConfig] = useContext(ConfigContext);

  const [loading, SetLoading] = useState(true);

  const {
    RowerFees,
    SchoolFees,
    MaxSchoolFees,
    LateRowerFees,
    LateSchoolFees,
  } = config.cpConfig;

  var noOfRowers = rowers?.length;

  var deadline = new Date(config.cpConfig.SecondDeadlineDate);
  var currentDate = new Date();
  let difference = currentDate.getTime() - deadline.getTime();
  let daysOverDeadline = Math.floor(difference / (1000 * 3600 * 24));

  useEffect(() => {
    SetLoading(true); // Start loading when the effect is triggered

    Promise.all([GetUnAssignedStudents(), GetRowers()])
      .then(([unassignedStudentsRes, rowersRes]) => {
        setUnassignedStudents(unassignedStudentsRes.data);
        SetRowers(rowersRes.data);
      })
      .finally(() => {
        SetLoading(false); // Set loading to false after both promises resolve
      });
  }, []);

  if (loading) {
    return (
      <div>
        <h1 style={{ fontSize: "1.5em" }}>Loading</h1>
        <Spin size="large" />
      </div>
    );
  }

  let nf = new Intl.NumberFormat("en-US");

  var totalrowerFees = RowerFees * noOfRowers;
  var totalschoolfees =
    noOfRowers * SchoolFees > MaxSchoolFees
      ? Number(MaxSchoolFees)
      : SchoolFees * noOfRowers;
  var totalfees = totalrowerFees + totalschoolfees;
  var lateFees = 0;

  if (daysOverDeadline > 0) {
    // lateFees = totalfees * 2;
    lateFees =
      lateFees +
      (daysOverDeadline - 1) * (LateSchoolFees + noOfRowers * LateRowerFees);

    totalfees = totalfees + lateFees;
  }

  // Handlers
  const handleUploadChange = (info) => {
    let newFileList = [...info.fileList];
    newFileList = newFileList.slice(-1); // Limit to 1 file
    setFileList(newFileList);

    if (info.file.status === "done") {
      message.success(`${info.file.name} file uploaded successfully`);
      // setPaymentStatus("Pending Confirmation");
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} file upload failed.`);
    }
  };

  // Mock upload action
  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok");
    }, 1000);
  };

  // Status Badge Helper
  const renderStatusTag = () => {
    const paymentStatus = user.stagescomplete.includes("Payment");

    switch (paymentStatus) {
      case true:
        return (
          <Tag
            icon={<CheckCircleOutlined />}
            color="success"
            style={{ fontSize: "16px", padding: "5px 10px" }}
          >
            Paid
          </Tag>
        );
      case false:
        return (
          <Tag
            icon={<CloseCircleOutlined />}
            color="error"
            style={{ fontSize: "16px", padding: "5px 10px" }}
          >
            Not Paid
          </Tag>
        );
      default:
        return (
          <Tag
            icon={<CloseCircleOutlined />}
            color="error"
            style={{ fontSize: "16px", padding: "5px 10px" }}
          >
            Not Paid
          </Tag>
        );
    }
  };

  return (
    <div>
      <Row gutter={[24, 24]}>
        {/* Left Column: Registration Details & Fees */}
        <Col xs={24} lg={12} xl={12}>
          <Card
            bordered={false}
            style={{
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              borderRadius: "8px",
              height: "100%",
            }}
          >
            {/* Header & Status */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
              }}
            >
              <Title level={4} style={{ margin: 0 }}>
                Registration Details
              </Title>
              {renderStatusTag()}
            </div>

            {/* Configuration Section */}
            <div
              style={{
                marginBottom: "24px",
                padding: "20px",
                backgroundColor: "#fafafa",
                borderRadius: "8px",
                border: "1px solid #f0f0f0",
              }}
            >
              <Space
                direction="vertical"
                size="middle"
                style={{ width: "100%" }}
              >
                <Row align="middle" gutter={[16, 16]}>
                  <Col span={12}>
                    <Text strong>Number of Students:</Text>
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    <Text> {noOfRowers}</Text>
                  </Col>
                </Row>
              </Space>
            </div>

            {/* Fee Breakdown */}
            <Title level={5} style={{ marginTop: "20px" }}>
              Fee Breakdown
            </Title>

            <div style={{ marginBottom: "20px" }}>
              <Row gutter={[16, 8]} style={{ marginBottom: "8px" }}>
                <Col span={16}>
                  <Text type="secondary">Individual Entry Fee Rate:</Text>
                </Col>
                <Col span={8} style={{ textAlign: "right" }}>
                  <Text>Rs. {nf.format(RowerFees)} / Student</Text>
                </Col>
              </Row>
              {noOfRowers * SchoolFees >= MaxSchoolFees ? (
                <Row gutter={[16, 8]} style={{ marginBottom: "16px" }}>
                  <Col span={16}>
                    <Text type="secondary">School Entry Fee Rate:</Text>
                  </Col>
                  <Col span={8} style={{ textAlign: "right" }}>
                    <Text>Rs. {nf.format(MaxSchoolFees)}</Text>
                  </Col>
                </Row>
              ) : (
                <Row gutter={[16, 8]} style={{ marginBottom: "16px" }}>
                  <Col span={16}>
                    <Text type="secondary">
                      School Entry Fee Rate (Less than 10 rowers):
                    </Text>
                  </Col>
                  <Col span={8} style={{ textAlign: "right" }}>
                    <Text>Rs. {nf.format(SchoolFees)} / Student</Text>
                  </Col>
                </Row>
              )}

              <div
                style={{
                  backgroundColor: "#f9f9f9",
                  padding: "15px",
                  borderRadius: "4px",
                }}
              >
                <Row gutter={[16, 12]}>
                  <Col span={16}>
                    <Text strong>Individual Entry Fee Sub Total:</Text>
                  </Col>
                  <Col span={8} style={{ textAlign: "right" }}>
                    <Text>
                      Rs. {nf.format(noOfRowers * RowerFees).toLocaleString()}
                    </Text>
                  </Col>

                  <Col span={16}>
                    <Text strong>School Entry Fee Sub Total:</Text>
                  </Col>
                  <Col span={8} style={{ textAlign: "right" }}>
                    <Text>
                      Rs.{" "}
                      {noOfRowers * SchoolFees >= MaxSchoolFees
                        ? nf.format(MaxSchoolFees)
                        : nf.format(noOfRowers * SchoolFees).toLocaleString()}
                    </Text>
                  </Col>

                  <Col span={24}>
                    <Divider style={{ margin: "10px 0" }} />
                  </Col>

                  <Col span={16}>
                    <Title level={4} style={{ margin: 0 }}>
                      Total Fee:
                    </Title>
                  </Col>
                  <Col span={8} style={{ textAlign: "right" }}>
                    <Title level={4} style={{ margin: 0 }}>
                      Rs. {totalfees.toLocaleString()}
                    </Title>
                  </Col>
                </Row>
              </div>
            </div>

            {/* Challan Download */}
            <div style={{ textAlign: "center", marginTop: "30px" }}>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                size="large"
                disabled={totalfees === 0}
                style={{ width: "100%", height: "48px", fontSize: "16px" }}
                onClick={async () => {
                  const dd = await GetFeeChallanDD(
                    user.schoolname,
                    new Date().toLocaleDateString("en-GB"),
                    noOfRowers,
                    `Rs. ${nf.format(totalrowerFees)}`,
                    `Rs. ${nf.format(totalschoolfees)}`,
                    `Rs. ${nf.format(totalfees)}`,
                    true,
                    lateFees ? `Rs. ${nf.format(lateFees)}` : 0,
                    config.cpConfig
                  );
                  var pdf = pdfMake.createPdf(dd);
                  console.log(pdf);
                  pdf.download("Fee Challan");
                }}
              >
                Download Fee Challan
              </Button>
            </div>
          </Card>
        </Col>

        {/* Right Column: Payment Actions */}
        <Col xs={24} lg={12} xl={12}>
          <Card
            bordered={false}
            style={{
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              borderRadius: "8px",
              height: "100%",
            }}
          >
            <Title level={4} style={{ marginBottom: "20px" }}>
              Payment Method
            </Title>

            <Radio.Group
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              buttonStyle="solid"
              style={{ width: "100%", marginBottom: "24px", display: "flex" }}
            >
              <Radio.Button
                value="cash"
                style={{ flex: 1, textAlign: "center" }}
              >
                Cash
              </Radio.Button>
              <Radio.Button
                value="bank"
                style={{ flex: 1, textAlign: "center" }}
              >
                Bank Transfer
              </Radio.Button>
              <Radio.Button
                value="cheque"
                style={{ flex: 1, textAlign: "center" }}
              >
                Cheque
              </Radio.Button>
            </Radio.Group>

            {/* Cash Content */}
            {paymentMethod === "cash" && (
              <div style={{ animation: "fadeIn 0.5s" }}>
                <Card
                  type="inner"
                  title={
                    <span>
                      <WalletOutlined /> Pay in Cash
                    </span>
                  }
                  headStyle={{
                    backgroundColor: "#fffbe6",
                    borderBottom: "1px solid #ffe58f",
                  }}
                  style={{ backgroundColor: "#fffbe6", borderColor: "#ffe58f" }}
                >
                  <Text>
                    Please visit the Regatta office at KBC to pay fees in cash
                    to Head Coach Asghar Ali or Abid Rehman. Bring your printed
                    fee challan with you.
                  </Text>
                  <br />
                  <br />
                  <Space direction="vertical">
                    <div>
                      <Text type="secondary">Timings:</Text>
                      <br />
                      <Text strong>Mon - Sat (9:00 AM - 5:00 PM)</Text>
                    </div>
                  </Space>
                </Card>
              </div>
            )}

            {/* Cheque Content */}
            {paymentMethod === "cheque" && (
              <div style={{ animation: "fadeIn 0.5s" }}>
                <Card
                  type="inner"
                  title={
                    <span>
                      <FileProtectOutlined /> Pay via Cheque
                    </span>
                  }
                  headStyle={{
                    backgroundColor: "#f0f5ff",
                    borderBottom: "1px solid #adc6ff",
                  }}
                  style={{ backgroundColor: "#f0f5ff", borderColor: "#adc6ff" }}
                >
                  <Space direction="vertical" size="large">
                    <div>
                      <Text type="secondary">Make Payable To:</Text>
                      <br />
                      <Title
                        level={4}
                        style={{ marginTop: 0, color: "#1d39c4" }}
                      >
                        Karachi Boat Club
                      </Title>
                    </div>
                    <div>
                      <Text>
                        Submit the cheque, along with your printed fee challan,
                        at the Regatta office to Head Coach Asghar Ali or Abid
                        Rehman.
                      </Text>
                    </div>
                  </Space>
                </Card>
              </div>
            )}

            {/* Bank Transfer Content */}
            {paymentMethod === "bank" && (
              <div style={{ animation: "fadeIn 0.5s" }}>
                <Card
                  type="inner"
                  title={
                    <span>
                      <BankOutlined /> Bank Details
                    </span>
                  }
                  headStyle={{
                    backgroundColor: "#f6ffed",
                    borderBottom: "1px solid #b7eb8f",
                  }}
                  style={{
                    marginBottom: "24px",
                    backgroundColor: "#f6ffed",
                    borderColor: "#b7eb8f",
                  }}
                >
                  <Space direction="vertical" style={{ width: "100%" }}>
                    <Row>
                      <Col span={8}>
                        <Text type="secondary">Bank:</Text>
                      </Col>
                      <Col span={16}>
                        <Text strong>Bank Al-Habib Limited</Text>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={8}>
                        <Text type="secondary">Title:</Text>
                      </Col>
                      <Col span={16}>
                        <Text strong>Karachi Boat Club</Text>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={8}>
                        <Text type="secondary">Acc No:</Text>
                      </Col>
                      <Col span={16}>
                        <Text strong>1255-0081-010250-01-1</Text>
                      </Col>
                    </Row>
                    <Row>
                      <Text strong>Note:</Text>
                      <Text>
                        For reconciliation purposes, please email the following
                        information as soon as the payment has been made to:
                        <br />
                        accounts@kbc.org.pk, accounts2@kbc.org.pk <br />
                        <br />
                        or send a screenshot on Karachi Boat Club WhatsApp:
                        <br />
                        0318-1056663, 0334-2633494
                      </Text>
                    </Row>

                    <Collapse ghost size="small">
                      <Panel header="SWIFT, IBAN & Address" key="1">
                        <Space direction="vertical" style={{ width: "100%" }}>
                          <Row>
                            <Col span={8}>
                              <Text type="secondary">IBAN:</Text>
                            </Col>
                            <Col span={16}>
                              <Text strong style={{ fontSize: "12px" }}>
                                PK45BAHL1255008101025001
                              </Text>
                            </Col>
                          </Row>
                          <Row>
                            <Col span={8}>
                              <Text type="secondary">SWIFT Code:</Text>
                            </Col>
                            <Col span={16}>
                              <Text strong>BAHLPPK</Text>
                            </Col>
                          </Row>
                          <Row>
                            <Col span={8}>
                              <Text type="secondary">Branch:</Text>
                            </Col>
                            <Col span={16}>
                              <Text strong>
                                Bank Al-Habib Limited, Chartered Accountants
                                Avenue, Karachi
                              </Text>
                            </Col>
                          </Row>
                          <Row>
                            <Col span={8}>
                              <Text type="secondary">Address:</Text>
                            </Col>
                            <Col span={16}>
                              <Text strong>
                                Sub Plot No. 23/FT-3/V, Shop No. 10, Clifton
                                View Apartment, Chartered Accountants Avenue,
                                Frere Town, Clifton, Block 8, Karachi.
                              </Text>
                            </Col>
                          </Row>
                        </Space>
                      </Panel>
                    </Collapse>
                  </Space>
                </Card>

                {/*          <Divider /> */}

                {/* Upload Payment Proof - ONLY for Bank Transfer */}
                {/*       <div>
                  <Title level={5}>Confirm Payment</Title>
                  <Alert
                    message="Upload proof of payment to complete registration."
                    type="info"
                    showIcon
                    style={{ marginBottom: "15px" }}
                  />

                  <Upload
                    customRequest={dummyRequest}
                    onChange={handleUploadChange}
                    fileList={fileList}
                    maxCount={1}
                  >
                    <Button icon={<UploadOutlined />} block size="large">
                      Upload Payment Confirmation
                    </Button>
                  </Upload>
                </div> */}
              </div>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
